import unescapedText from 'I18n/utils/unescapedText';
import Small from 'UIComponents/elements/Small';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { BreezeLoadingDot } from 'ai-components-ui-library/components/loading/BreezeLoadingDot';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import SimpleMessageSection from '../../conversational/SimpleMessageSection';
import SimpleMessageSectionDisabled from '../../conversational/SimpleMessageSectionDisabled';
import CopilotPreviewHeader from './CopilotPreviewHeader';
import SummaryTimestamp from './SummaryTimestamp';
import BreezeRefreshButton from './breeze/BreezeRefreshButton';
import { useIsDisabledByAccessControls } from './hooks/useIsDisabledByAccessControls';
import { getCopyButtonText } from './utils/CopyButton';
import { useShouldShowActions } from '../../../hooks/useShouldShowActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CopilotPreviewInner = ({
  children,
  hasAIBadge,
  onRefresh,
  onPositiveFeedback,
  onNegativeFeedback,
  onQuickActionClick,
  previewTitle,
  response,
  isLoading,
  usageTracker,
  usageTrackerAppLocationKey,
  overrideDisabled,
  isStreaming
}) => {
  const {
    isDisabledByAccessControls
  } = useIsDisabledByAccessControls();
  const copyMessage = getCopyButtonText(response);
  const isDisabled = (overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled) || isDisabledByAccessControls;
  const shouldShowActions = useShouldShowActions();
  const hasResponse = response && response.sections && response.sections.length > 0;
  if (isDisabled) {
    var _overrideDisabled$dis, _overrideDisabled$dis2;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        marginBlockEnd: "100",
        children: [/*#__PURE__*/_jsx(CopilotPreviewHeader, {
          previewTitle: previewTitle
        }), hasAIBadge && /*#__PURE__*/_jsx(AIBadge, {})]
      }), /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        gap: "200",
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Tooltip, {
          delay: BREEZE_TOOLTIP_DELAY,
          title: unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledTooltip'),
          disabled: Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledSubtitle),
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: "text-core-subtle",
            children: (_overrideDisabled$dis = overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledSubtitle) !== null && _overrideDisabled$dis !== void 0 ? _overrideDisabled$dis : unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledSubtitle')
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsxs(Small, {
          tagName: "div",
          "data-test-id": "preview-summary",
          children: [hasResponse && response.sections.map(section => /*#__PURE__*/_jsx(SimpleMessageSectionDisabled, {
            section: section
          }, section.id)), !hasResponse && /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: (_overrideDisabled$dis2 = overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledDescription) !== null && _overrideDisabled$dis2 !== void 0 ? _overrideDisabled$dis2 : unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledDescription')
            })
          }), isStreaming && /*#__PURE__*/_jsx(BreezeLoadingDot, {})]
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      marginBlockEnd: "100",
      children: [/*#__PURE__*/_jsx(CopilotPreviewHeader, {
        previewTitle: previewTitle
      }), hasAIBadge && /*#__PURE__*/_jsx(AIBadge, {})]
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(SummaryTimestamp, {
        timestamp: response && response.timestamp
      }), !isLoading && !isStreaming && /*#__PURE__*/_jsx(BreezeRefreshButton, {
        onRefresh: onRefresh
      })]
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "200",
      children: [/*#__PURE__*/_jsxs(Small, {
        tagName: "div",
        "data-test-id": "preview-summary",
        children: [hasResponse && response.sections.map(section => /*#__PURE__*/_jsx(SimpleMessageSection, {
          section: section,
          onQuickActionClick: onQuickActionClick,
          usageTracker: usageTracker
        }, section.id)), isStreaming && /*#__PURE__*/_jsx(BreezeLoadingDot, {})]
      }), children && shouldShowActions && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: children
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: response && response.messageId && /*#__PURE__*/_jsx(InlineFeedback, {
        copyMessage: copyMessage,
        messageId: response.messageId,
        onPositiveFeedback: onPositiveFeedback,
        onNegativeFeedback: onNegativeFeedback,
        threadId: response.threadId,
        usageTracker: usageTracker,
        usageTrackerAppLocationKey: usageTrackerAppLocationKey
      })
    })]
  });
};
export default CopilotPreviewInner;